import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  stockTransferService,
  StockTransfer,
} from "../../services/stock-transfer.service";
import { showToast } from "../../lib/toast";
import { ConfirmDialog } from "../../components/ui/ConfirmDialog";

const STATUS_COLORS: Record<string, string> = {
  DRAFT: "bg-gray-100 text-gray-800",
  APPROVED: "bg-blue-100 text-blue-800",
  IN_TRANSIT: "bg-yellow-100 text-yellow-800",
  RECEIVED: "bg-green-100 text-green-800",
  CANCELLED: "bg-red-100 text-red-800",
  REJECTED: "bg-red-100 text-red-800",
};

export function StockTransfersPage() {
  const navigate = useNavigate();
  const [transfers, setTransfers] = useState<StockTransfer[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    type: "approve" | "send";
    id: string;
  }>({ isOpen: false, type: "approve", id: "" });
  const [isProcessing, setIsProcessing] = useState(false);
  const limit = 20;

  useEffect(() => {
    loadTransfers();
  }, [page, statusFilter]);

  const loadTransfers = async () => {
    try {
      setLoading(true);
      const response = await stockTransferService.getAll({
        page,
        limit,
        status: statusFilter !== "all" ? statusFilter : undefined,
      });
      setTransfers(response.data || []);
      setTotal(response.pagination?.total || 0);
    } catch (err: any) {
      showToast.error(
        err.response?.data?.message || "Failed to load stock transfers",
      );
      setTransfers([]);
    } finally {
      setLoading(false);
    }
  };

  const handleApproveClick = (id: string) => {
    setConfirmModal({ isOpen: true, type: "approve", id });
  };

  const handleSendClick = (id: string) => {
    setConfirmModal({ isOpen: true, type: "send", id });
  };

  const handleConfirm = async () => {
    setIsProcessing(true);
    try {
      if (confirmModal.type === "approve") {
        await stockTransferService.approve(confirmModal.id);
        showToast.success("Transfer approved");
      } else {
        await stockTransferService.send(confirmModal.id);
        showToast.success("Transfer marked as in-transit");
      }
      loadTransfers();
    } catch (err: any) {
      showToast.error(
        err.response?.data?.message ||
          `Failed to ${confirmModal.type} transfer`,
      );
    } finally {
      setIsProcessing(false);
      setConfirmModal({ isOpen: false, type: "approve", id: "" });
    }
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Stock Transfers</h1>
          <p className="text-sm text-gray-500 mt-1">
            Inter-store inventory transfers
          </p>
        </div>
        <button
          onClick={() => navigate("/stock-transfers/create")}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          + New Transfer
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <div className="flex flex-wrap gap-4">
          <select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setPage(1);
            }}
            className="px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">All Status</option>
            <option value="DRAFT">Draft</option>
            <option value="APPROVED">Approved</option>
            <option value="IN_TRANSIT">In Transit</option>
            <option value="RECEIVED">Received</option>
            <option value="CANCELLED">Cancelled</option>
          </select>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-gray-500">Loading...</div>
        ) : transfers.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            No stock transfers found
          </div>
        ) : (
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Transfer #
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  From Store
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  To Store
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Items
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {transfers.map((transfer) => (
                <tr key={transfer.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">
                      {transfer.transferNumber}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {new Date(transfer.transferDate).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {transfer.sourceStore?.name || "-"}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {transfer.destStore?.name || "-"}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {transfer._count?.items || transfer.items?.length || 0}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={`px-2 py-1 text-xs rounded-full ${
                        STATUS_COLORS[transfer.status] || "bg-gray-100"
                      }`}
                    >
                      {transfer.status.replace("_", " ")}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <button
                      onClick={() =>
                        navigate(`/stock-transfers/${transfer.id}`)
                      }
                      className="text-blue-600 hover:text-blue-900 mr-3"
                    >
                      View
                    </button>
                    {transfer.status === "DRAFT" && (
                      <button
                        onClick={() => handleApproveClick(transfer.id)}
                        className="text-green-600 hover:text-green-900 mr-3"
                      >
                        Approve
                      </button>
                    )}
                    {transfer.status === "APPROVED" && (
                      <button
                        onClick={() => handleSendClick(transfer.id)}
                        className="text-yellow-600 hover:text-yellow-900 mr-3"
                      >
                        Send
                      </button>
                    )}
                    {transfer.status === "IN_TRANSIT" && (
                      <button
                        onClick={() =>
                          navigate(`/stock-transfers/${transfer.id}/receive`)
                        }
                        className="text-green-600 hover:text-green-900"
                      >
                        Receive
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="bg-gray-50 px-6 py-3 flex items-center justify-between border-t">
            <div className="text-sm text-gray-700">
              Page {page} of {totalPages}
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="px-3 py-1 text-sm border rounded disabled:opacity-50"
              >
                Previous
              </button>
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="px-3 py-1 text-sm border rounded disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      <ConfirmDialog
        isOpen={confirmModal.isOpen}
        onClose={() =>
          setConfirmModal({ isOpen: false, type: "approve", id: "" })
        }
        onConfirm={handleConfirm}
        title={
          confirmModal.type === "approve" ? "Approve Transfer" : "Send Transfer"
        }
        message={
          confirmModal.type === "approve"
            ? "Are you sure you want to approve this stock transfer request?"
            : "Are you sure you want to mark this transfer as sent/in-transit?"
        }
        confirmText={confirmModal.type === "approve" ? "Approve" : "Send"}
        cancelText="Cancel"
        variant="info"
        isLoading={isProcessing}
      />
    </div>
  );
}

export default StockTransfersPage;
